import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Paper, Stack, Typography } from '@esgian/esgianui';
import { DISPLAY_UNIT } from '@constants/index';
import { TextWithTooltipIcon } from '@components';
import {
  getMainPageSlice
} from '@store/features/filters/MainPageSlice/MainPageSlice';
import { getValue } from '@helpers';
import { findEquipmentSignal } from '@api/helpers';
import { useDisplayUnit, useTheme, useSeriesBuilder } from '@hooks';
import SimpleChartJSBarChart from '@components/Charts/SimpleChartJSBarChart/SimpleChartJSBarChart';
import { getRigSlice } from '@store/features/filters/RigSlice/RigSlice';
import { NoDataAvailable } from '@components/Display';

// TODO Use same component for both overview and equipment section
function TotalForPeriodChartJS({ equipmentData, equipmentSignalsData, equipmentConfig, loading }) {
  const { getUnitTitle, displayUnit } = useDisplayUnit();
  const { theme } = useTheme();
  const { getSeriesData } = useSeriesBuilder();
  const {
    palette: { charts }
  } = theme;

  const { selectedRig } = useSelector(getMainPageSlice);
  const rigStateSettings = useSelector(state => getRigSlice(state, selectedRig.rigName));
  const selectedEquipmentName = rigStateSettings?.selectedEquipmentName;
  const isEquipmentSelected = rigStateSettings?.isEquipmentSelected;

  const {
    allComponentsResponse,
  } = useMemo(() => {
    if (!equipmentData || !equipmentConfig?.equipmentDetails) 
      return {
        allComponentsResponse: { unit: '', series: [] }
      };

    const { componentCalculations } = equipmentData;
    let max = 0;
    let displayDataList = [];
    
    const { equipmentDetails } = equipmentConfig;
    equipmentDetails.map(({ equipmentName, chartConfig }) => {
      const signal = findEquipmentSignal(componentCalculations, equipmentName, displayUnit);
      const data = signal?.timeSeriesValues || {};
      if (!data || Object.keys(data).length === 0) return null;
      const hasNonZero = Object.values(data).some((v) => getValue(v) !== 0);
      if (!hasNonZero) return null;
      let displayName = chartConfig.displayName === 'Unknown component' ? equipmentName : chartConfig.displayName;
      let tot = Object.values(data).reduce((a, b) => a + getValue(b), 0);
      max = max > tot ? max : tot;

      let { defaultArray, convertedArray } = getSeriesData([tot], displayUnit);
      defaultArray = defaultArray.map(val => { return { x: displayName, y: val }});
      convertedArray = convertedArray.map(val => { return { x: displayName, y: val }});
      let displayData = {
          label: displayName,
          type: 'bar',
          order: chartConfig.order,
          group: '',
          borderColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
          backgroundColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
          borderSkipped: true,
          stack: 1,
          defaultArray: defaultArray,
          data: [],
          convertedArray: convertedArray
        }
      displayDataList.push(displayData);
    });

    displayDataList = displayDataList.sort((a, b) => a.order - b.order);

    const { title, converted } = getUnitTitle(max);

    return {
      allComponentsResponse: {
        unit: title,
        series: displayDataList.map(d => { return { 
          ...d,
          data: converted ? d.convertedArray : d.defaultArray, 
          unit: title, 
          converted: converted 
        }})
      }
    };
  }, [displayUnit, equipmentData, equipmentConfig]);

  const { componentResponse } = useMemo(() => {
    if (!equipmentSignalsData || !isEquipmentSelected || !selectedEquipmentName) 
      return { componentResponse: { unit: '', series: [] }};

    const { componentCalculations } = equipmentSignalsData;
    let displayDataList = [];
    let max = 0;
    componentCalculations
      .find(e => e.name === selectedEquipmentName)
      .signals
      .filter(s => s.metricType === MetricTypes[displayUnit])
      .map(({ name, chartConfig, timeSeriesValues }) => 
      {
        let data = timeSeriesValues;
        if (!data) return null;
        let displayName = chartConfig.displayName === 'Unknown component' ? name : chartConfig.displayName;
        let tot = Object.values(data).reduce((a, b) => a + getValue(b), 0);
        max = max > tot ? max : tot;

        let { defaultArray, convertedArray } = getSeriesData([tot], displayUnit);
        defaultArray = defaultArray.map(val => { return { x: displayName, y: val }});
        convertedArray = convertedArray.map(val => { return { x: displayName, y: val }});
        let displayData = {
            label: displayName,
            type: 'bar',
            order: chartConfig.order,
            group: '',
            borderColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
            backgroundColor: charts?.bgColour ?? charts[chartConfig.bgColourGroupName][chartConfig.bgColourGroupIndex] ?? '#000000',
            borderSkipped: true,
            stack: 1,
            defaultArray: defaultArray,
            data: [],
            convertedArray: convertedArray
          }
        displayDataList.push(displayData);
      });

    displayDataList = displayDataList.sort((a, b) => a.order - b.order);

    const { title, converted } = getUnitTitle(max);

    return {
      componentResponse: {
        unit: title,
        series: displayDataList.map(d => { return { 
          ...d,
          data: converted ? d.convertedArray : d.defaultArray, 
          unit: title, 
          converted: converted 
        }})
      }
    };
  }, [displayUnit, equipmentSignalsData, isEquipmentSelected, selectedEquipmentName]);

  const getSubTitle = useMemo(() => {
    switch (displayUnit) {
      case DISPLAY_UNIT.CO2:
        return 'Total equipment CO2e emissions in period';
      case DISPLAY_UNIT.NOX:
        return 'Total equipment NOx emissions in period';
      case DISPLAY_UNIT.FUEL:
        return 'Total equipment fuel consumption in period';
      case DISPLAY_UNIT.ENERGY:
        return 'Total equipment energy consumption in period';
    }
  }, [displayUnit]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1}>
        <TextWithTooltipIcon
          tooltipText={`Total consumption or emissions for each equipment in the selected time period.`}
          label={<Typography variant={'h6'}>Equipment summary</Typography>}
        />
        <Typography variant="body2">{getSubTitle}</Typography>
        {!loading && NoDataAvailable.hasNoData(allComponentsResponse?.series) && NoDataAvailable.hasNoData(componentResponse?.series) ? (
          <NoDataAvailable height="360px" />
        ) : (
        <SimpleChartJSBarChart
          loading={loading}
          series={isEquipmentSelected && componentResponse.series ? componentResponse?.series : allComponentsResponse?.series}
          id={'equipment-summary-chart'}
          height={'360px'}
          unit={isEquipmentSelected && componentResponse.series ? componentResponse?.unit : allComponentsResponse?.unit}
          tooltipTitle={displayUnit}
          fixTooltipPosition={true}
          positionTooltipAboveCursor={true}
          />
        )}
      </Stack>
    </Paper>
  );
}

TotalForPeriodChartJS.propTypes = {
  equipmentData: PropTypes.object,
  equipmentSignalsData: PropTypes.object,
  equipmentConfig: PropTypes.object,
  loading: PropTypes.bool
};

TotalForPeriodChartJS.defaultProps = {
  equipmentData: null,
  equipmentSignalsData: null, 
  equipmentConfig: null,
  loading: false
};

const MetricTypes = Object.freeze({
  Undefined: 'Undefined',
  Power: 'Power',
  Energy: 'Energy',
  Fuel: 'Fuel',
  NOx: 'NOx',
  CO2: 'CO2',
  TotP: 'TotP',
  EnergyCons: 'EnergyCons'
});

export default TotalForPeriodChartJS;
